// ~/apps/themusicchurch-app/frontend/src/components/Common/Card.jsx

import React from "react";
import styled, { css } from "styled-components";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const CardWrapper = styled(motion.div)`
  background: ${(props) => props.theme.colors.white};
  border-radius: ${(props) => (props.rounded ? "16px" : "8px")};
  overflow: hidden;
  box-shadow: ${(props) =>
    props.elevated ? props.theme.shadows.lg : props.theme.shadows.md};
  transition: all ${(props) => props.theme.transitions.base};

  ${(props) =>
    props.hoverable &&
    css`
      cursor: pointer;

      &:hover {
        transform: translateY(-5px);
        box-shadow: ${props.theme.shadows.xl};
      }
    `}

  ${(props) =>
    props.bordered &&
    css`
      border: 1px solid ${props.theme.colors.gray[200]};
    `}
`;

const CardLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: block;
`;

const CardImage = styled.div`
  width: 100%;
  height: ${(props) => props.height || "250px"};
  background: url(${(props) => props.src}) center/cover;
  position: relative;

  ${(props) =>
    props.overlay &&
    css`
      &::after {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: ${props.overlayColor ||
        "linear-gradient(to bottom, transparent, rgba(0,0,0,0.5))"};
      }
    `}
`;

const CardBadge = styled.div`
  position: absolute;
  top: ${(props) => props.theme.spacing.md};
  ${(props) => (props.position === "left" ? "left" : "right")}: ${(props) =>
    props.theme.spacing.md};
  background: ${(props) => props.theme.colors.accent};
  color: ${(props) => props.theme.colors.dark};
  padding: ${(props) => props.theme.spacing.xs}
    ${(props) => props.theme.spacing.sm};
  border-radius: 20px;
  font-size: ${(props) => props.theme.fontSizes.sm};
  font-weight: 600;
  z-index: 1;
`;

const CardContent = styled.div`
  padding: ${(props) =>
    props.noPadding ? 0 : props.theme.spacing[props.padding || "xl"]};
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const CardTitle = styled.h3`
  color: ${(props) => props.theme.colors.primary};
  font-size: ${(props) => props.theme.fontSizes[props.size || "2xl"]};
  font-weight: 700;
  margin-bottom: ${(props) => props.theme.spacing.sm};
`;

const CardSubtitle = styled.p`
  color: ${(props) => props.theme.colors.gray[600]};
  font-size: ${(props) => props.theme.fontSizes.base};
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const CardBody = styled.div`
  color: ${(props) => props.theme.colors.gray[700]};
  line-height: 1.7;
`;

const CardFooter = styled.div`
  padding: ${(props) => props.theme.spacing.lg}
    ${(props) => props.theme.spacing.xl};
  background: ${(props) => props.theme.colors.gray[50]};
  border-top: 1px solid ${(props) => props.theme.colors.gray[200]};
  display: flex;
  justify-content: ${(props) => props.align || "space-between"};
  align-items: center;
  gap: ${(props) => props.theme.spacing.md};
`;

const CardAction = styled.button`
  background: none;
  border: none;
  color: ${(props) => props.theme.colors.accent};
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${(props) => props.theme.spacing.sm};
  transition: all ${(props) => props.theme.transitions.fast};

  &:hover {
    color: ${(props) => props.theme.colors.secondary};
    gap: ${(props) => props.theme.spacing.md};
  }
`;

const Card = ({
  children,
  image,
  imageHeight,
  imageOverlay,
  imageOverlayColor,
  badge,
  badgePosition = "left",
  title,
  titleSize = "2xl",
  subtitle,
  body,
  footer,
  footerAlign,
  to,
  href,
  onClick,
  hoverable = false,
  elevated = false,
  bordered = false,
  rounded = true,
  padding = "xl",
  noPadding = false,
  motionProps = {},
  ...rest
}) => {
  const content = (
    <>
      {image && (
        <div style={{ position: "relative" }}>
          <CardImage
            src={image}
            height={imageHeight}
            overlay={imageOverlay}
            overlayColor={imageOverlayColor}
          />
          {badge && <CardBadge position={badgePosition}>{badge}</CardBadge>}
        </div>
      )}

      <CardContent noPadding={noPadding} padding={padding}>
        {title && (
          <CardHeader>
            <div>
              <CardTitle size={titleSize}>{title}</CardTitle>
              {subtitle && <CardSubtitle>{subtitle}</CardSubtitle>}
            </div>
          </CardHeader>
        )}

        {body && <CardBody>{body}</CardBody>}
        {children}
      </CardContent>

      {footer && <CardFooter align={footerAlign}>{footer}</CardFooter>}
    </>
  );

  const cardMotionProps = {
    whileHover: hoverable ? { y: -5 } : {},
    transition: { duration: 0.2 },
    ...motionProps,
  };

  if (to) {
    return (
      <CardWrapper
        hoverable={hoverable}
        elevated={elevated}
        bordered={bordered}
        rounded={rounded}
        {...cardMotionProps}
        {...rest}
      >
        <CardLink to={to}>{content}</CardLink>
      </CardWrapper>
    );
  }

  if (href) {
    return (
      <CardWrapper
        as="a"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        hoverable={hoverable}
        elevated={elevated}
        bordered={bordered}
        rounded={rounded}
        {...cardMotionProps}
        {...rest}
      >
        {content}
      </CardWrapper>
    );
  }

  return (
    <CardWrapper
      onClick={onClick}
      hoverable={hoverable}
      elevated={elevated}
      bordered={bordered}
      rounded={rounded}
      style={{ cursor: onClick ? "pointer" : "default" }}
      {...cardMotionProps}
      {...rest}
    >
      {content}
    </CardWrapper>
  );
};

// Export subcomponents for flexible usage
Card.Image = CardImage;
Card.Content = CardContent;
Card.Title = CardTitle;
Card.Subtitle = CardSubtitle;
Card.Body = CardBody;
Card.Footer = CardFooter;
Card.Action = CardAction;

export default Card;
